import { Box, HStack, Input, Text, Textarea, VStack } from "@chakra-ui/react";
import {
  AddCircle as AddCircleIcon,
  Delete as DeleteIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { FieldArray, getIn, useField, useFormikContext } from "formik";
import { type ChangeEvent, FC, useCallback, useEffect, useRef } from "react";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { SymbolOutlineButton } from "~/src/components/common/Button";
import { FormikDateField } from "~/src/components/common/forms/FormikDateField/FormikDateField";
import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";
import { FormikSwitchField } from "~/src/components/common/forms/FormikSwitchField";
import { FormikTextField } from "~/src/components/common/forms/FormikTextField";
import { IconButton } from "~/src/components/common/IconButton";
import { Switch } from "~/src/components/common/Switch";
import { DeliverDayOfWeeksFieldArray } from "~/src/components/features/deliverDayOfWeek";
import {
  DeliverUrlListField,
  TargetCookieListField,
} from "~/src/components/features/global/DistributionSetingForm";
import { ScheduleListField } from "~/src/components/features/global/ScheduleListField";
import { TargetContentListField } from "~/src/components/features/popup/PopupGroupForm/TargetContentListField";

import {
  isPopupImportDisplayPopupAgainActive,
  mapDistributionStatus,
  parsePopupImportCsvBoolString,
} from "../convertFunctions";
import type { PopupImportColumnDef } from "../popupImportColumnModel";
import {
  formatStringList,
  parseStringList,
} from "../popupImportFieldSerialization";
import { usePopupImportScheduleOptions } from "../PopupImportScheduleOptionsContext";
import { popupImportSelectWithMenuPortalProps } from "../popupImportSelectMenuPortal";
import type { PopupImportFormValues } from "../validationSchema";

import { PopupImportActionTimingCell } from "./PopupImportActionTimingCell";
import { PopupImportDisplayPopupAgainImageCell } from "./PopupImportDisplayPopupAgainImageCell";
import {
  PopupImportFunnelPageSelectCell,
  PopupImportFunnelSelectCell,
} from "./PopupImportFunnelPageCells";
import { PopupImportImageSlotCell } from "./PopupImportImageSlotCell";
import { PopupImportSegmentFilterCell } from "./PopupImportSegmentFilterCell";
import { PopupImportSourceIdsListCell } from "./PopupImportSourceIdsListCell";

const STICKY_DELETE_W = "44px";
const STICKY_ROW_W = "52px";

const selectWithMenuPortalProps = popupImportSelectWithMenuPortalProps;

/** Chakra inputs aligned with `TextField` fill mode (40px, funnel import table). */
const fillLineInputProps = {
  h: "40px",
  fontSize: "13px",
  lineHeight: "16px",
  letterSpacing: "0.04em",
  bg: "#FAFAFA",
  border: "1px solid #D0D0D0",
  borderRadius: "2px",
  px: "10px",
  _focus: {
    borderColor: "#D0D0D0",
    boxShadow: "0 0 0 1px #D0D0D0",
  },
} as const;

export type Props = {
  popupIndex: number;
  onDelete: () => void;
  visibleColumns: PopupImportColumnDef[];
};

type CellProps = {
  base: string;
  col: PopupImportColumnDef;
  popupIndex: number;
};

const StringListCell: FC<{ name: string }> = ({ name }) => {
  const [field] = useField<string[]>(name);
  const { setFieldValue } = useFormikContext<PopupImportFormValues>();
  const display = formatStringList(field.value);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      void setFieldValue(name, parseStringList(e.target.value));
    },
    [name, setFieldValue]
  );

  return (
    <Input {...fillLineInputProps} value={display} onChange={handleChange} />
  );
};

const REFERRER_MATCH_OP_OPTIONS = [
  { label: "完全一致", value: "EQUAL" },
  { label: "含む", value: "CONTAINS" },
] as const;

const ReferrerFieldArrayCell: FC<{ name: string }> = ({ name }) => {
  const [field] = useField<{ referrer: string; matchOp: string }[]>(name);
  const rows = field.value ?? [];

  return (
    <FieldArray
      name={name}
      // eslint-disable-next-line react/jsx-no-bind
      render={(arrayHelpers) => (
        <Box minW="280px" maxW="400px">
          <VStack spacing="4px" align="stretch">
            {rows.map((_, index) => (
              <HStack key={index} spacing="4px">
                <Box flex={1}>
                  <FormikTextField
                    type="text"
                    mode="fill"
                    name={`${name}.${index}.referrer`}
                    placeholder="google.co.jp"
                  />
                </Box>
                <Box width="110px">
                  <FormikSelectField
                    name={`${name}.${index}.matchOp`}
                    options={[...REFERRER_MATCH_OP_OPTIONS]}
                    {...selectWithMenuPortalProps}
                  />
                </Box>
                <Box>
                  <IconButton
                    size="24px"
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => arrayHelpers.remove(index)}
                    icon={<DeleteOutlineIcon />}
                  />
                </Box>
              </HStack>
            ))}
            <Box>
              <SymbolOutlineButton
                icon={<AddCircleIcon />}
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() =>
                  arrayHelpers.push({ referrer: "", matchOp: "EQUAL" })
                }
              >
                追加
              </SymbolOutlineButton>
            </Box>
          </VStack>
        </Box>
      )}
    />
  );
};

const SyncedGroupNameCell: FC<{ base: string }> = ({ base }) => {
  const { setFieldValue } = useFormikContext<PopupImportFormValues>();
  const name = `${base}.popupGroupName`;
  const [field] = useField<string>(name);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      void setFieldValue(name, v);
      void setFieldValue(`${base}.group.name`, v);
    },
    [base, name, setFieldValue]
  );

  return (
    <Input
      {...fillLineInputProps}
      name={field.name}
      value={field.value ?? ""}
      onBlur={field.onBlur}
      onChange={handleChange}
    />
  );
};

const DistributionStatusSwitchCell: FC<{ name: string }> = ({ name }) => {
  const [field, , helpers] = useField<string>(name);
  const mapped = mapDistributionStatus(field.value ?? "") ?? "ACTIVE";
  const isChecked = mapped === "ACTIVE";
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      void helpers.setValue(e.target.checked ? "ACTIVE" : "INACTIVE");
    },
    [helpers]
  );
  return <Switch name={name} isChecked={isChecked} onChange={handleChange} />;
};

const CsvBoolStringSwitchCell: FC<{ name: string }> = ({ name }) => {
  const [field, , helpers] = useField<string>(name);
  const isChecked = parsePopupImportCsvBoolString(field.value ?? "");
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      void helpers.setValue(e.target.checked ? "true" : "false");
    },
    [helpers]
  );
  return <Switch name={name} isChecked={isChecked} onChange={handleChange} />;
};

const ReadonlyCell: FC<{ name: string }> = ({ name }) => {
  const [field] = useField<string>(name);
  return (
    <Input
      {...fillLineInputProps}
      value={field.value ?? ""}
      isReadOnly
      title="エクスポート値（参照のみ）"
    />
  );
};

const ACTION_OPTIONS = [
  { label: "（未選択）", value: "" },
  { label: "登録", value: "CREATE" },
  { label: "編集", value: "UPDATE" },
] as const;

const PopupImportCompactTextareaCell: FC<{ name: string }> = ({ name }) => {
  const [{ value, onBlur, onChange }, { error, touched }] =
    useField<string>(name);
  const borderColor = touched && error ? "#E21010" : "#D0D0D0";
  return (
    <Textarea
      name={name}
      value={value ?? ""}
      onBlur={onBlur}
      onChange={onChange}
      rows={1}
      size="sm"
      fontSize="13px"
      lineHeight="16px"
      letterSpacing="0.04em"
      borderRadius="2px"
      border="1px solid"
      borderColor={borderColor}
      bg="#FAFAFA"
      px="10px"
      py="8px"
      minH="40px"
      h="auto"
      resize="vertical"
      _focus={{
        borderColor,
        boxShadow: `0 0 0 1px ${borderColor}`,
      }}
    />
  );
};

const DISTRIBUTION_TYPE_OPTIONS = [
  { label: "シングル配信", value: "false" },
  { label: "マルチ配信", value: "true" },
] as const;

const DateFieldCell: FC<{ name: string; placeholder: string }> = ({
  name,
  placeholder,
}) => (
  <Box minW="130px">
    <FormikDateField name={name} placeholder={placeholder} />
  </Box>
);

const ScheduleListCell: FC<{ name: string }> = ({ name }) => {
  const { scheduleOptions } = usePopupImportScheduleOptions();
  const [field] = useField<string[]>(name);
  const optionValues = new Set(scheduleOptions.map((o) => o.value));
  const augmented = [...scheduleOptions];
  for (const id of field.value ?? []) {
    if (id && !optionValues.has(id)) {
      augmented.push({
        label: id,
        value: id,
        _utcStartMin: null,
        _utcEndMin: null,
      });
    }
  }
  return (
    <Box minW="260px" py="2px">
      <ScheduleListField
        name={name}
        label=""
        options={augmented}
        selectProps={selectWithMenuPortalProps}
      />
    </Box>
  );
};

const DeliverDayOfWeeksCell: FC<{ name: string }> = ({ name }) => (
  <Box minW="400px" maxW="560px">
    <DeliverDayOfWeeksFieldArray
      name={name}
      weekOfMonthSelectProps={selectWithMenuPortalProps}
    />
  </Box>
);

function PopupImportCell({ base, col, popupIndex }: CellProps) {
  const fullName = `${base}.${col.formikPath}`;

  switch (col.cellKind) {
    case "funnel_select":
      return (
        <PopupImportFunnelSelectCell name={fullName} popupIndex={popupIndex} />
      );
    case "funnel_page_select":
      return (
        <PopupImportFunnelPageSelectCell
          name={fullName}
          popupIndex={popupIndex}
        />
      );
    case "formik_date":
      return (
        <DateFieldCell
          name={fullName}
          placeholder={
            col.id === "start_date" ? "開始日を選択" : "終了日を選択"
          }
        />
      );
    case "schedule_list":
      return <ScheduleListCell name={fullName} />;
    case "source_id_list":
      return (
        <PopupImportSourceIdsListCell name={fullName} popupIndex={popupIndex} />
      );
    case "deliver_day_of_weeks":
      return <DeliverDayOfWeeksCell name={fullName} />;
    case "distribution_type_select":
      return (
        <FormikSelectField
          name={fullName}
          options={[...DISTRIBUTION_TYPE_OPTIONS]}
          {...selectWithMenuPortalProps}
        />
      );
    case "deliver_kind_select":
      return (
        <FormikSelectField
          name={fullName}
          options={GraphQLEnums.DeliverKind}
          {...selectWithMenuPortalProps}
        />
      );
    case "content_kind_select":
      return (
        <FormikSelectField
          name={fullName}
          options={[
            { label: "（未選択）", value: "" },
            ...GraphQLEnums.PopupContentKind,
          ]}
          {...selectWithMenuPortalProps}
        />
      );
    case "action_select":
      return (
        <FormikSelectField
          name={fullName}
          options={[...ACTION_OPTIONS]}
          {...selectWithMenuPortalProps}
        />
      );
    case "action_timing_block":
      return <PopupImportActionTimingCell popupIndex={popupIndex} />;
    case "text":
      return <FormikTextField mode="fill" type="text" name={fullName} />;
    case "textarea":
      return <PopupImportCompactTextareaCell name={fullName} />;
    case "enum_select": {
      const opts = col.selectOptions;
      if (!opts?.length) return null;
      return (
        <FormikSelectField
          name={fullName}
          options={[...opts]}
          {...selectWithMenuPortalProps}
        />
      );
    }
    case "graphql_enum_multi": {
      const multiKey = col.graphqlEnumMultiKey;
      if (!multiKey) return null;
      return (
        <PopupImportSegmentFilterCell
          name={fullName}
          graphqlEnumMultiKey={multiKey}
          menuPortalProps={selectWithMenuPortalProps}
          minW={col.minW}
        />
      );
    }
    case "readonly":
      return <ReadonlyCell name={fullName} />;
    case "string_list":
      return <StringListCell name={fullName} />;
    case "referrer_list":
      return <ReferrerFieldArrayCell name={fullName} />;
    case "exclude_referrer_list":
      return <ReferrerFieldArrayCell name={fullName} />;
    case "json_array":
      return (
        <Box minW="280px" maxW="360px" py="2px">
          <TargetContentListField
            name={fullName}
            label=""
            selectProps={selectWithMenuPortalProps}
          />
        </Box>
      );
    case "url_list":
      return (
        <Box minW="260px" maxW="360px" py="2px">
          <DeliverUrlListField name={fullName} label="" />
        </Box>
      );
    case "cookies_cell":
      return (
        <Box minW="300px" maxW="400px" py="2px">
          <TargetCookieListField name={fullName} label="" />
        </Box>
      );
    case "synced_group_name":
      return <SyncedGroupNameCell base={base} />;
    case "switch_bool":
      return <FormikSwitchField name={fullName} />;
    case "distribution_status_switch":
      return <DistributionStatusSwitchCell name={fullName} />;
    case "csv_bool_switch":
      return <CsvBoolStringSwitchCell name={fullName} />;
    case "image_slot": {
      const slot = col.imageSlot;
      if (slot === undefined) return null;
      return <PopupImportImageSlotCell base={base} slot={slot} />;
    }
    case "display_popup_again_image":
      return <PopupImportDisplayPopupAgainImageCell base={base} />;
    default:
      return null;
  }
}

export const PopupImportRow: FC<Props> = ({
  popupIndex,
  onDelete,
  visibleColumns,
}) => {
  const rowBg = popupIndex % 2 === 0 ? "white" : "#F7FAFC";
  const { values, setFieldValue, errors } =
    useFormikContext<PopupImportFormValues>();
  const action = values.popups[popupIndex]?.action;
  const base = `popups.${popupIndex}`;
  const rowFallbackImportError = getIn(errors, `${base}._importRow`) as
    | string
    | undefined;
  const prevActionRef = useRef<typeof action | undefined>(undefined);

  useEffect(() => {
    const prev = prevActionRef.current;
    if (action === "CREATE" && prev !== "CREATE") {
      void setFieldValue(`${base}.popupId`, "");
    }
    prevActionRef.current = action;
  }, [action, base, setFieldValue]);

  return (
    <Box as="tr" bg={rowBg} borderBottom="1px solid #E2E8F0">
      <Box
        as="td"
        w={STICKY_DELETE_W}
        minW={STICKY_DELETE_W}
        p="4px 8px"
        verticalAlign="top"
        position="sticky"
        left={0}
        bg={rowBg}
        zIndex={2}
      >
        <Box
          as="button"
          type="button"
          onClick={onDelete}
          color="#E53E3E"
          display="inline-flex"
          alignItems="center"
          aria-label="行を削除"
          title="行を削除"
          _hover={{ color: "#C53030" }}
        >
          <DeleteIcon fontSize="small" />
        </Box>
      </Box>
      <Box
        as="td"
        w={STICKY_ROW_W}
        minW={STICKY_ROW_W}
        p="4px 8px"
        verticalAlign="top"
        position="sticky"
        left={STICKY_DELETE_W}
        bg={rowBg}
        zIndex={2}
      >
        <VStack alignItems="flex-start" spacing="2px" maxW="200px">
          <Text
            fontSize="11px"
            fontWeight="bold"
            color="#4A5568"
            whiteSpace="nowrap"
          >
            {popupIndex + 1}
          </Text>
          {rowFallbackImportError ? (
            <Text
              fontSize="10px"
              color="red.500"
              lineHeight="1.3"
              whiteSpace="normal"
            >
              {rowFallbackImportError}
            </Text>
          ) : null}
        </VStack>
      </Box>
      {visibleColumns.map((col) => {
        const hidePopupIdForCreate =
          col.id === "popup_id" && action === "CREATE";
        const hidePopupGroupIdForCreate =
          col.id === "popup_group_id" && action === "CREATE";
        const displayAgainActive = isPopupImportDisplayPopupAgainActive(
          values.popups[popupIndex]?.popup?.displayPopupAgain
        );
        const hideAgainImageCell =
          col.id === "again_img" && !displayAgainActive;
        const hideCell =
          hidePopupIdForCreate ||
          hidePopupGroupIdForCreate ||
          hideAgainImageCell;
        return (
          <Box
            key={col.id}
            as="td"
            p="4px 8px"
            verticalAlign="top"
            bg={rowBg}
            minW={col.minW ?? "88px"}
            maxW={
              col.cellKind === "textarea" ||
              col.cellKind === "enum_select" ||
              col.cellKind === "graphql_enum_multi" ||
              col.cellKind === "image_slot" ||
              col.cellKind === "display_popup_again_image" ||
              col.cellKind === "funnel_select" ||
              col.cellKind === "funnel_page_select" ||
              col.cellKind === "deliver_day_of_weeks" ||
              col.cellKind === "action_timing_block"
                ? col.cellKind === "deliver_day_of_weeks"
                  ? "560px"
                  : col.cellKind === "action_timing_block"
                  ? "280px"
                  : "220px"
                : undefined
            }
            aria-hidden={hideCell ? true : undefined}
          >
            {hideCell ? (
              <Text fontSize="13px" color="transparent" userSelect="none">
                {"\u00a0"}
              </Text>
            ) : (
              <PopupImportCell base={base} col={col} popupIndex={popupIndex} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};
